import React from "react";
import { Info } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTranslation } from "@/lib/i18n";

/**
 * OpportunityScoreScale — Sub-pass 2A (2026-02-17), follow-up
 * corrective pass (2026-02-18).
 *
 * Renders a compact, reversible 0-100 scale under the AI Opportunity
 * Score on landing-page Market Snapshot cards.  Canonical band
 * thresholds mirror `backend/poc/valuation_guards.py`:
 *
 *     ai_score <  45  → forces Risk
 *     ai_score >= 60  → threshold for Opportunity (with other gates)
 *     ai_score >= 75  → threshold for Top Opportunity (with other gates)
 *
 * ---
 * Corrective pass — the previous version placed the information-tooltip
 * trigger BETWEEN the "0" and "100" endpoints, which visually read as
 * a mid-scale marker.  The trigger is now removed from the scale
 * footer entirely; the endpoints show a purely numerical `0 / 50 /
 * 100` reference (50 is the numerical midpoint, NOT a classification
 * threshold — the four coloured bands still mark the only canonical
 * thresholds).  The `<InfoTrigger />` is exported separately and
 * rendered by the parent card next to the "AI Opportunity Score"
 * label.
 */
const CANONICAL_BANDS = [
  { from: 0,  to: 45,  key: "risk",         color: "rgba(255, 61, 166, 0.28)" },
  { from: 45, to: 60,  key: "neutral",      color: "rgba(255, 255, 255, 0.12)" },
  { from: 60, to: 75,  key: "opportunity",  color: "rgba(43, 255, 138, 0.28)" },
  { from: 75, to: 100, key: "top",          color: "rgba(43, 255, 138, 0.48)" },
];

function clamp01(score) {
  const n = Number(score);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n));
}

function bandForScore(pos) {
  return pos < 45
    ? "risk"
    : pos < 60
      ? "neutral"
      : pos < 75
        ? "opportunity"
        : "top";
}

/**
 * OpportunityScoreScaleInfo — the accessible information affordance
 * that discloses "Final classification also considers valuation,
 * upside, risk and deterministic guardrails.".  Exported separately
 * so parent cards can render it beside the "AI Opportunity Score"
 * label instead of leaving it on the scale footer.  Keyboard-,
 * mouse- and touch-accessible via Radix `Tooltip` + a native
 * `<button>` trigger with an `aria-label`.
 */
export function OpportunityScoreScaleInfo({
  testId = "opportunity-score-scale-info",
  className = "",
}) {
  const { t } = useTranslation();
  const disclosure = t("landing.snapshot_score_scale_disclosure");
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={disclosure}
            className={`inline-flex items-center justify-center rounded-full w-4 h-4 text-muted-foreground/80 hover:text-foreground hover:bg-white/[0.06] focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-primary/60 transition-colors ${className}`}
            data-testid={testId}
          >
            <Info className="w-3 h-3" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="top"
          className="max-w-[260px] text-[11px] leading-[1.45]"
          data-testid={`${testId}-tooltip`}
        >
          {disclosure}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export default function OpportunityScoreScale({
  score,
  testId = "opportunity-score-scale",
  markerColor = "#F5F5F5",
  compact = false,
}) {
  const { t } = useTranslation();
  const pos = clamp01(score);
  const bandKey = bandForScore(pos);
  const trackHeight = compact ? "h-[3px]" : "h-[4px]";
  const markerSize  = compact ? "w-1.5 h-1.5" : "w-2 h-2";
  const scoreLabel = t("landing.snapshot_score_scale_aria", {
    score: Math.round(pos),
    band: t(`landing.snapshot_score_scale_band_${bandKey}`),
  });

  return (
    <div
      className="w-full"
      data-testid={testId}
      data-score={Math.round(pos)}
      data-band={bandKey}
    >
      <div
        role="img"
        aria-label={scoreLabel}
        className={`relative w-full ${trackHeight} rounded-full overflow-visible bg-white/[0.04] border border-white/[0.06]`}
      >
        {/* Canonical score-only bands — mirrored bit-for-bit from
            valuation_guards.py.  Never invented, never editorialised. */}
        <div className="absolute inset-0 flex rounded-full overflow-hidden" aria-hidden="true">
          {CANONICAL_BANDS.map((b) => (
            <div
              key={b.key}
              style={{
                width: `${b.to - b.from}%`,
                background: b.color,
              }}
              data-testid={`${testId}-band-${b.key}`}
            />
          ))}
        </div>
        {/* Position marker — the ONLY element that communicates the
            current score's position on the scale. */}
        <div
          className={`absolute top-1/2 -translate-y-1/2 ${markerSize} rounded-full ring-2 ring-black/40`}
          style={{
            left: `calc(${pos}% - ${compact ? 3 : 4}px)`,
            background: markerColor,
            boxShadow: `0 0 8px ${markerColor}88`,
          }}
          data-testid={`${testId}-marker`}
          aria-hidden="true"
        />
      </div>
      {/* Numerical endpoint labels 0 / 50 / 100.  The 50 is a purely
          numerical midpoint reference — NOT a classification threshold. */}
      <div className="mt-1 relative text-[9px] text-muted-foreground/70 tabular">
        <span
          className="absolute left-0"
          aria-hidden="true"
          data-testid={`${testId}-endpoint-0`}
        >0</span>
        <span
          className="absolute left-1/2 -translate-x-1/2"
          aria-hidden="true"
          data-testid={`${testId}-endpoint-50`}
        >50</span>
        <span
          className="absolute right-0"
          aria-hidden="true"
          data-testid={`${testId}-endpoint-100`}
        >100</span>
        {/* Reserved height for the absolute-positioned endpoint labels. */}
        <span className="block invisible" aria-hidden="true">0</span>
      </div>
    </div>
  );
}
